import { MediaCollection } from '@pub.tech/ardplayer-client-br24';
import { differenceInSeconds, formatISO, subMilliseconds } from 'date-fns';
import { useMemo, ReactNode } from 'react';

import { MediaContextPlaybackStatus } from '@/cutils/context/MediaContext';
import { buildMediaStructuredData } from '@/cutils/seo/schema-org/buildModuleMediaStructuredData';
import { resize } from '@/utils/image/resize';

import { ArticleModuleLivestreamFragment } from '../Article/Modules/__generated__/ArticleModuleLivestream.generated';

import { MediaLivestreamStatus } from './LiveStreamStatus';
import { MediaPlayer, MediaPlayerMedia, MediaPlayerPosterImage } from './MediaPlayer';
import { useLivestreamUpdateStatus } from './use-livestream-countdown';

export type LivestreamVideoFile = NonNullable<
	NonNullable<
		NonNullable<NonNullable<NonNullable<ArticleModuleLivestreamFragment['livestream']>['programme']>['videoFiles']>['nodes']
	>[number]
>;

type Props = {
	copyright?: string | null;
	isHero?: boolean;
	isPriority?: boolean;
	liveStreamHlsUrl: string | null;
	livestreamStartDate: string | null;
	livestreamEndDate: string | null;
	publicationDate?: string | null;
	moduleId: number | 'rundschau';
	thumbnailAltText?: string | null;
	thumbnailCopyright?: string | null;
	thumbnailLink?: string | null;
	thumbnailUrl?: string | null;
	title?: string | null;
	videos?: LivestreamVideoFile[];
	onPlayStatusChange?(status: MediaContextPlaybackStatus): void;
	enableZoomEffect?: boolean;
	/**
	 * The duration of the prelive window, before the livestream start date.
	 *
	 * This setting will allow to show the play button before the livestream starts.
	 *
	 * Default: `0`.
	 */
	preliveDuration?: number;
	sizes: string;
	playButtonTitle?: string;
	posterImageTitle?: ReactNode;
};

export function MediaLivestream({
	copyright,
	isHero,
	isPriority,
	liveStreamHlsUrl,
	livestreamStartDate,
	livestreamEndDate,
	moduleId,
	thumbnailAltText,
	thumbnailCopyright,
	thumbnailLink,
	thumbnailUrl,
	title,
	videos: videosOnDemand = [],
	onPlayStatusChange,
	publicationDate,
	enableZoomEffect = false,
	sizes,
	playButtonTitle,
	posterImageTitle: customPosterImageTitle,
	preliveDuration = 0,
}: Props) {
	const {
		startDate,
		endDate,
		status: livestreamStatus,
		url,
		videos,
	} = useLivestreamUpdateStatus(moduleId, livestreamStartDate, livestreamEndDate, preliveDuration, liveStreamHlsUrl, videosOnDemand);

	const durationInSecond = startDate && endDate ? differenceInSeconds(endDate, startDate) : null;

	const showPlayButton =
		// if livestream is past and we have on demand videos available, we can play the on demand video
		// if the livesteram is ongoing and we already have a recording, we can play the on demand video
		((livestreamStatus === 'PAST' || livestreamStatus === 'NOW_LIVE') && videos.length > 0) ||
		// if we are in the livestream prelive window
		livestreamStatus === 'PRE_LIVE' ||
		// if we are currently live
		livestreamStatus === 'NOW_LIVE';

	const mediaCollection: MediaCollection | undefined = useMemo(() => {
		// the player will not show a play button when no media collection is supplied
		if (!showPlayButton) {
			return undefined;
		}

		const isLive =
			// if the livestream is ongoing but recordings are available, we switch to past mode
			(livestreamStatus === 'NOW_LIVE' && videos.length === 0) || livestreamStatus === 'PRE_LIVE';

		let streamsMedia: MediaCollection['streams'][number]['media'];

		let live: MediaCollection['live'] = undefined;
		let broadcastingType: 'OnDemand' | 'Live';

		if (isLive) {
			// can not play livestream without a url
			if (!url) {
				return undefined;
			}

			broadcastingType = 'Live';

			// return the livestream media information
			streamsMedia = [
				{
					url,
					mimeType: 'application/vnd.apple.mpegurl',
					isAdaptiveQualitySelectable: true,
					audios: [
						{
							kind: 'standard',
							languageCode: 'de',
						},
					],
				},
			];

			/*
			 * Setting this value to a number > 0  will switch the player into the live mode.
			 * The value of this option has no other effect at the moment.
			 */
			live = {
				dvrWindowSeconds: 600,
				availFromDateTime: startDate ? formatISO(subMilliseconds(startDate, preliveDuration)) : undefined,
				availToDateTime: endDate?.toISOString(),
			};
		} else if (videos.length > 0) {
			broadcastingType = 'OnDemand';

			// if we do have associated videos return the videos
			streamsMedia = videos
				.filter(({ publicLocation }) => publicLocation)
				.map((video) => {
					return {
						url: video.publicLocation!,
						mimeType: 'application/vnd.apple.mpegurl',
						isAdaptiveQualitySelectable: true,
						aspectRatio: '16:9',
						audios: [
							{
								kind: 'standard',
								languageCode: 'de',
							},
						],
					};
				});
		} else {
			// if we do not have any information about streams or videos return no media collection at all
			return undefined;
		}

		return {
			// the meta information will be displayed within the information panel of
			// the player
			meta: {
				images: [],
				title: '',
				synopsis: title ?? '',
				clipSourceName: copyright ?? undefined,
			},
			streams: [
				{
					kind: 'main',
					media: streamsMedia,
				},
			],
			live,
			pluginData: {
				'trackingPiano@web': {
					avContent: {
						av_content_id: `${moduleId}`,
						av_content_type: 'Video',
						av_broadcasting_type: broadcastingType,
						av_player_position: isHero ? 'Header' : 'Body',
					},
				},
			},
		};
	}, [showPlayButton, livestreamStatus, videos, title, copyright, moduleId, isHero, url, startDate, preliveDuration, endDate]);

	const structuredData = useMemo(
		() =>
			buildMediaStructuredData({
				duration: Math.ceil(durationInSecond || 0),
				publicationDate,
				thumbnail: thumbnailUrl ? resize(thumbnailUrl, 'landscape', 'xl') : undefined,
				title,
				type: 'video',
				url: (livestreamStatus === 'PAST' ? videos?.[0]?.publicLocation : url) ?? '',
				publication: {
					'@type': 'BroadcastEvent',
					name: title ?? 'Next Broadcast',
					isLiveBroadcast: true,
					startDate: livestreamStartDate ?? '',
					endDate: livestreamEndDate ?? '',
				},
			}),
		[durationInSecond, publicationDate, thumbnailUrl, title, livestreamStatus, videos, url, livestreamStartDate, livestreamEndDate]
	);

	let posterImageTitle: ReactNode;
	if (customPosterImageTitle) {
		posterImageTitle = customPosterImageTitle;
	} else {
		posterImageTitle = <MediaLivestreamStatus startDate={startDate} endDate={endDate} />;
	}

	const media: MediaPlayerMedia = {
		structuredData,
		mediaCollection,
		type: 'livestream',
	};

	const posterImage: MediaPlayerPosterImage = {
		sizes,
		altText: thumbnailAltText,
		copyright: thumbnailCopyright,
		enableZoomEffect,
		imageUrl: thumbnailUrl,
		link: thumbnailLink,
		isHero,
		isPriority,
		playButtonTitle,
		title: posterImageTitle,
		showGradient: !!customPosterImageTitle,
	};

	return <MediaPlayer media={media} posterImage={posterImage} rowId={moduleId} onPlayStatusChange={onPlayStatusChange} />;
}
